import type {
  DataContext,
  Report,
  ReportTemplate,
  TemplateSection,
  FieldDefinition,
} from './schemas';
import { newId } from '../utils/ids';
import { buildKeypath } from './idGenerator';
import { parsePipes, applyPipes, evaluateTemplateExpression } from './templateExpressions';
import { formatIsoDate } from '@/utils/date';

const DEFAULT_VISIT_DATE = () => formatIsoDate(new Date());
const DEFAULT_SITE_LOCATION_FALLBACK =
  '3 impianti frigoriferi distinti (I1 – I2 – I3)\nIn vani dedicati, posizionati al piano terra';

export const COMPANY_MISSING_PLACEHOLDER = '[Azienda non disponibile]';
export const SITE_MISSING_PLACEHOLDER = '[Stabilimento non disponibile]';
export const UNIT_MISSING_PLACEHOLDER = '[Impianto non disponibile]';

/**
 * Variable resolver for Report templates
 * Performs string interpolation and converts templates into reports with flat data structure
 */
export class VariableResolver {
  constructor(private readonly context: DataContext) {}

  /**
   * Resolve a single source template string with the context
   * Used for live resolution of field values in draft reports
   */
  resolveSource(source: string, fieldType: FieldDefinition['type']): unknown {
    const resolved = this.interpolate(source);
    return this.coerceValueToType(resolved, fieldType);
  }

  /**
   * Resolves a ReportTemplate into a Report with flat data.values structure.
   *
   * This method performs three main tasks:
   * 1. Interpolates template variables ({{company.name}}, {{site.address}}, etc.)
   * 2. Generates keypaths for all fields (e.g., "sec_company.fld_name")
   * 3. Populates initial values based on field types and sources
   *
   * @param template - The template to resolve
   * @returns A new Report instance with resolved values in data.values
   *
   * @example
   * ```typescript
   * const template = await TemplatesRepo.getDefault();
   * const context: DataContext = {
   *   company: await CompaniesRepo.get(companyId),
   *   site: await SitesRepo.getSite(siteId),
   *   variables: { today: '2025-09-30', inspectorName: 'Mario Rossi' }
   * };
   * const resolver = new VariableResolver(context);
   * const report = resolver.resolveTemplate(template);
   *
   * // report.data.values now contains:
   * // {
   * //   "sec_company.fld_name": "Acme Corp",  // From company.name
   * //   "sec_site.fld_address": "Via Roma 1", // From site.address
   * //   "sec_units.row_0.fld_name": "Unit A",  // From site.refrigerationUnits[0].name
   * // }
   *
   * await ReportsRepo.upsert(report);
   * ```
   */
  resolveTemplate(template: ReportTemplate, options: { reportId?: string } = {}): Report {
    const now = new Date().toISOString();
    const visitDate =
      typeof this.context.variables?.today === 'string'
        ? this.context.variables.today
        : DEFAULT_VISIT_DATE();
    const inspectorName =
      typeof this.context.variables?.inspectorName === 'string'
        ? String(this.context.variables.inspectorName)
        : 'Ispettore';

    // Build flat data.values from template sections
    const values: Record<string, unknown> = {};

    for (const section of template.sections) {
      this.populateValuesFromSection(section, values);
    }

    const presetReportId = options.reportId ?? this.getReportIdFromContext();
    const reportId = presetReportId ?? newId();
    const siteId = this.context.site?.id;

    if (!siteId) {
      throw new Error('Site is required to create a report.');
    }

    return {
      id: reportId,
      version: 4,
      templateId: template.id,
      templateVersion: template.version,
      companyId: this.context.company?.id ?? 'unknown-company',
      siteId,
      status: 'draft',
      createdAt: now,
      updatedAt: now,
      meta: {
        title: this.resolveRequiredTemplate(template.name),
        visitDate,
        inspector: inspectorName,
        company: this.context.company?.name,
        siteName: this.context.site?.name ?? '',
        siteLocation: this.context.site?.sedeOperativa ?? this.context.site?.address,
      },
      data: {
        values,
        comments: {},
        highlights: {},
        nonConformities: [],
      },
    };
  }

  /**
   * Populate flat values from a template section
   */
  private populateValuesFromSection(
    section: TemplateSection,
    values: Record<string, unknown>,
    indices?: number[]
  ): void {
    // Handle repeating sections
    if (section.type === 'repeating' && section.repeat) {
      const arrayData = this.getValueByPath(section.repeat.forEach);
      if (Array.isArray(arrayData)) {
        arrayData.forEach((item, index) => {
          // Populate values for this array item with temporary context
          for (const field of section.fields) {
            const keypath = buildKeypath(section.id, field.id, [...(indices ?? []), index]);
            values[keypath] = this.resolveFieldValueWithContext(field, {
              ...this.context,
              [section.repeat!.as]: item,
            } as DataContext);
          }

          this.populateViewBlocks(
            section,
            values,
            {
              ...this.context,
              [section.repeat!.as]: item,
            } as DataContext,
            [...(indices ?? []), index]
          );
        });
      }
      return;
    }

    // Handle static sections
    for (const field of section.fields) {
      const keypath = buildKeypath(section.id, field.id, indices);
      values[keypath] = this.resolveFieldValue(field);
    }

    this.populateViewBlocks(section, values, this.context, indices);
  }

  private populateViewBlocks(
    section: TemplateSection,
    values: Record<string, unknown>,
    context: DataContext,
    indices?: number[]
  ): void {
    if (!section.viewBlocks?.length) {
      return;
    }

    for (const block of section.viewBlocks) {
      if (block.type === 'table') {
        const keypath = buildKeypath(section.id, block.id, indices);
        const resolved = evaluateTemplateExpression(
          block.rowSource,
          context as Record<string, unknown>
        );

        if (Array.isArray(resolved)) {
          values[keypath] = this.cloneSnapshot(resolved);
        } else {
          values[keypath] = [];
        }
      }
    }
  }

  private cloneSnapshot(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.cloneSnapshot(item));
    }

    if (value && typeof value === 'object') {
      const entries = Object.entries(value as Record<string, unknown>).map(
        ([key, val]) => [key, this.cloneSnapshot(val)] as const
      );
      return Object.fromEntries(entries);
    }

    return value;
  }

  /**
   * Resolve a field's value from its source binding or default
   */
  private resolveFieldValue(field: FieldDefinition): unknown {
    return this.resolveFieldValueWithContext(field, this.context);
  }

  /**
   * Resolve a field's value with a specific context (used for repeating sections)
   */
  private resolveFieldValueWithContext(field: FieldDefinition, context: DataContext): unknown {
    // If field has a source binding, resolve it
    if (field.source) {
      const resolved = this.interpolateWithContext(field.source, context);
      // Convert to appropriate type based on field type
      return this.coerceValueToType(resolved, field.type);
    }

    // Return default value based on type
    return this.getDefaultValueForType(field.type);
  }

  /**
   * Coerce a string value to the appropriate type
   */
  private coerceValueToType(value: string, type: FieldDefinition['type']): unknown {
    if (value === '' || value === 'undefined' || value === 'null') {
      return this.getDefaultValueForType(type);
    }

    switch (type) {
      case 'number':
        const num = parseFloat(value);
        return isNaN(num) ? 0 : num;
      case 'bool':
        return value === 'true' || value === '1';
      case 'date':
        // Keep as string in ISO format
        return value;
      default:
        return value;
    }
  }

  /**
   * Get default value for a field type
   */
  private getDefaultValueForType(type: FieldDefinition['type']): unknown {
    switch (type) {
      case 'number':
        return 0;
      case 'bool':
        return null;
      case 'date':
        return '';
      case 'choice':
        return '';
      default:
        return '';
    }
  }

  /**
   * Template string interpolation helpers
   */
  private resolveRequiredTemplate(value: string): string {
    const resolved = this.interpolate(value);
    return resolved.length ? resolved : value;
  }

  private interpolate(template: string): string {
    return this.interpolateWithContext(template, this.context);
  }

  private interpolateWithContext(template: string, context: DataContext): string {
    return template.replace(/{{\s*((?:[^}]|\}(?!\}))+)\s*}}/g, (_match, expression) => {
      const trimmed = expression.trim();

      // Check if expression contains pipes
      const pipeIndex = trimmed.indexOf('|');
      if (pipeIndex === -1) {
        // No pipes - simple path resolution
        const resolved = this.getValueByPathFromContext(trimmed, context);
        if (resolved === undefined || resolved === null) {
          const placeholder = this.resolvePlaceholder(trimmed, context);
          if (placeholder) {
            return placeholder;
          }
          if (trimmed === 'site.location') {
            return DEFAULT_SITE_LOCATION_FALLBACK;
          }
          return '';
        }
        return this.formatResolvedValue(resolved);
      }

      // Split expression into path and pipes
      const path = trimmed.substring(0, pipeIndex).trim();
      const pipesExpression = trimmed.substring(pipeIndex + 1).trim();

      // Resolve base value
      let value = this.getValueByPathFromContext(path, context);
      if (value === undefined || value === null) {
        const placeholder = this.resolvePlaceholder(path, context);
        if (placeholder) {
          return placeholder;
        }
        if (path === 'site.location') {
          value = DEFAULT_SITE_LOCATION_FALLBACK;
        } else {
          value = '';
        }
      }

      if (this.isFieldValue(value)) {
        value = this.formatResolvedValue(value);
      }

      // Parse and apply pipes
      const pipes = parsePipes(pipesExpression);
      const result = applyPipes(value, pipes);

      // Convert result to string
      if (result === undefined || result === null) return '';
      return this.formatResolvedValue(result);
    });
  }

  private formatResolvedValue(value: unknown): string {
    if (value && typeof value === 'object') {
      const maybeFieldValue = value as {
        kind?: string;
        optionId?: unknown;
        text?: unknown;
      };
      if (maybeFieldValue.kind === 'option') {
        return typeof maybeFieldValue.optionId === 'string' ? maybeFieldValue.optionId : '';
      }
      if (maybeFieldValue.kind === 'custom' || maybeFieldValue.kind === 'text') {
        return typeof maybeFieldValue.text === 'string' ? maybeFieldValue.text : '';
      }
    }
    return String(value ?? '');
  }

  private isFieldValue(value: unknown): boolean {
    if (!value || typeof value !== 'object') return false;
    const maybeFieldValue = value as { kind?: unknown };
    return (
      maybeFieldValue.kind === 'option' ||
      maybeFieldValue.kind === 'custom' ||
      maybeFieldValue.kind === 'text'
    );
  }

  private resolvePlaceholder(path: string, context: DataContext): string | null {
    const [root] = path.split('.');
    const scoped = context as Record<string, unknown>;

    if (root === 'company') {
      const company = scoped.company;
      if (company === undefined || company === null) {
        return COMPANY_MISSING_PLACEHOLDER;
      }
      return null;
    }

    if (root === 'site') {
      const site = scoped.site;
      if (site === undefined || site === null) {
        return SITE_MISSING_PLACEHOLDER;
      }
      return null;
    }

    if (root === 'unit') {
      const unit = scoped.unit;
      if (unit === undefined || unit === null) {
        return UNIT_MISSING_PLACEHOLDER;
      }
      return null;
    }

    return null;
  }

  private getValueByPath(path: string): unknown {
    return this.getValueByPathFromContext(path, this.context);
  }

  private getValueByPathFromContext(path: string, context: DataContext): unknown {
    const segments = path.split('.');
    let current: unknown = context;

    for (const segment of segments) {
      if (current && typeof current === 'object' && segment in current) {
        current = (current as Record<string, unknown>)[segment];
      } else {
        return undefined;
      }
    }

    return current;
  }

  private getReportIdFromContext(): string | undefined {
    const reportValue = (
      this.context as {
        report?: { id?: unknown };
      }
    ).report;
    return typeof reportValue?.id === 'string' ? reportValue.id : undefined;
  }
}
